import type { ReactNode } from "react";
import { Button, Panel } from "@akaike/ui";
import { platforms, usePlatforms } from "./platforms";

// Per-platform pill styling (colour + logo), generated from the platform contract
// instead of hand-maintained `.plat.<name>` rules in qcs.css (row 17). The `.plat` base
// (sizing, monogram fallback) stays in the stylesheet; this only supplies each platform's
// `--pc` colour and logo `::before`, so a new platform's pill appears the moment its SPEC
// is registered — no CSS edit. Injected once, after the contract resolves.
function PlatformStyles() {
  const css = platforms()
    .map(
      (p) =>
        `.plat.${p.key}{--pc:${p.color};}\n` +
        `.plat.${p.key}::before{content:"";padding:0;min-width:17px;width:17px;` +
        `background-color:#fff;background-image:url(${p.logo_url});background-size:cover;` +
        `background-position:center;background-repeat:no-repeat;}`,
    )
    .join("\n");
  // biome-ignore lint/security/noDangerouslySetInnerHtml: static, backend-generated config CSS
  return <style dangerouslySetInnerHTML={{ __html: css }} />;
}

// Blocks first render until the platform contract (GET /v1/platforms) resolves, so the
// synchronous accessors in ./platforms (platformKeys, platformLabel, tiersFor, ...) are
// populated before any page reads them. Mirrors the RequireAuth loading gate; sits inside
// RequireAuth since the endpoint is session-guarded.
export function PlatformBoot({ children }: { children: ReactNode }) {
  const q = usePlatforms();
  if (q.isLoading) {
    return (
      <div className="center-screen">
        <div className="spinner" />
        <div className="muted">Loading platforms…</div>
      </div>
    );
  }
  // A failed fetch would otherwise fall through to render every page with empty
  // selectors and platformKeys()[0] === undefined — surface it instead of a broken UI.
  if (q.isError || !q.data?.length) {
    return (
      <div className="center-screen">
        <Panel alarm style={{ maxWidth: 460, padding: 26, textAlign: "center" }}>
          <h2 style={{ fontSize: 20, marginBottom: 10 }}>Platforms unavailable</h2>
          <p className="muted" style={{ marginBottom: 18 }}>
            Signed in, but the platform registry didn’t load. Is the control plane up?
          </p>
          <Button variant="outline" onClick={() => q.refetch()}>
            Retry
          </Button>
        </Panel>
      </div>
    );
  }
  return (
    <>
      <PlatformStyles />
      {children}
    </>
  );
}
